import { REPORT_CONSTANTS } from '../constants';

const { STATIC_REPORTS, REPORT_TYPES } = REPORT_CONSTANTS;

/**
 * Parses the current URL to extract report information
 */
const parseReportFromUrl = (pathname: string) => {
  // Handle legacy custom reports
  if (pathname.includes('/custom-reports/')) {
    const legacyId = pathname.split('/custom-reports/')[1];
    return {
      type: REPORT_TYPES.CUSTOM,
      id: legacyId,
    };
  }

  // Extract reportID from unified pattern: /reports/interviews -> interviews
  const reportId = pathname.split('/reports/')[1]?.split('/')[0];

  if (!reportId) {
    return {
      type: REPORT_TYPES.INVALID,
      id: '',
    };
  }

  // Check if it starts with numbers (custom report)
  if (/^\d+/.test(reportId)) {
    // Extract just the numeric ID for API calls
    const numericId = reportId.match(/^\d+/)?.[0] || reportId;
    return {
      type: REPORT_TYPES.CUSTOM,
      id: numericId,
    };
  }

  // Check if it's a known static report
  if (STATIC_REPORTS.includes(reportId)) {
    return {
      type: REPORT_TYPES.STATIC,
      id: reportId,
    };
  }

  // If it's alphabetic but not a known static report, it's invalid
  return {
    type: REPORT_TYPES.INVALID,
    id: reportId,
  };
};

/**
 * Sanitizes a string for use in URLs by removing/replacing problematic characters
 * Handles special characters like %, &, #, ?, etc. that can break URLs
 */
const sanitizeForUrl = (str: string): string => {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return (
    str
      .toLowerCase()
      .trim()
      // Replace any non-alphanumeric characters (including %, &, #, ?, etc.) with hyphens
      .replace(/[^a-z0-9]+/g, '-')
      // Replace multiple consecutive hyphens with single hyphen
      .replace(/-+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-|-$/g, '')
  );
};

/**
 * Generates a custom report URL based on the feReportsV3 flag
 * Uses proper sanitization to handle special characters safely
 */
const getCustomReportUrl = (
  reportId: string | number,
  reportName?: string,
  feReportsV3?: boolean
): string => {
  const id = String(reportId);

  if (feReportsV3 && reportName) {
    const sanitizedName = sanitizeForUrl(reportName);
    return sanitizedName ? `/reports/${id}-${sanitizedName}` : `/reports/${id}`;
  }

  return `/custom-reports/${id}`;
};

/**
 * Generates a static report URL
 */
const getStaticReportUrl = (reportId: string): string => {
  return `/reports/${reportId}`;
};

export const reportUtils = {
  parseReportFromUrl,
  sanitizeForUrl,
  getCustomReportUrl,
  getStaticReportUrl,
};
